import { useEffect, useState } from "react";
import { CanceledError } from "axios";
import {
  AdminCatalogProduct,
  fetchCatalogProducts,
} from "../api/fetchCatalogProducts";

export interface CatalogProductsFilters {
  searchTerm: string;
  categoryId: string;
}

interface PaginationState {
  pageIndex: number;
  pageSize: number;
}

interface SortingState {
  id: string;
  desc: boolean;
}

export function useCatalogProducts(refreshCounter: number) {
  const [products, setProducts] = useState<AdminCatalogProduct[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState<SortingState[]>([]);
  const [filters, setFilters] = useState<CatalogProductsFilters>({
    searchTerm: "",
    categoryId: "",
  });

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);

    const sort = sorting[0];
    fetchCatalogProducts(
      {
        searchTerm: filters.searchTerm || undefined,
        categoryId: filters.categoryId || undefined,
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        sortBy: (sort?.id as "name" | "sortOrder" | "createdAt") ?? "sortOrder",
        sortOrder: sort?.desc ? "DESC" : "ASC",
      },
      controller.signal,
    )
      .then((response) => {
        const total = response.meta?.totalCount ?? response.data.length;
        // Deleting the last row of the last page leaves pageIndex pointing
        // past the end — clamp back instead of rendering an empty table.
        if (
          response.data.length === 0 &&
          total > 0 &&
          pagination.pageIndex > 0
        ) {
          setPagination((prev) => ({
            ...prev,
            pageIndex: Math.max(0, Math.ceil(total / prev.pageSize) - 1),
          }));
          return;
        }
        setProducts(response.data);
        setTotalCount(total);
        setError(null);
        setLoading(false);
      })
      .catch((err) => {
        if (err instanceof CanceledError) return;
        setLoading(false);
      });

    return () => controller.abort();
  }, [pagination, sorting, filters, refreshCounter]);

  const handlePaginationChange = (newPagination: PaginationState) => {
    setPagination(newPagination);
  };

  const handleSortingChange = (
    updater: SortingState[] | ((prev: SortingState[]) => SortingState[]),
  ) => {
    setSorting((prev) =>
      typeof updater === "function" ? updater(prev) : updater,
    );
  };

  const handleFilterChange = (searchTerm: string, categoryId?: string) => {
    setFilters((prev) => ({
      searchTerm,
      categoryId: categoryId ?? prev.categoryId,
    }));
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  return {
    products,
    totalCount,
    loading,
    error,
    pagination,
    sorting,
    filters,
    handlePaginationChange,
    handleSortingChange,
    handleFilterChange,
  };
}
